// Lightweight wishlist stored in this browser (localStorage); no account needed.
const STORAGE_KEY = 'needle-eye:wishlist';
const CHANGE_EVENT = 'wishlist:change';

function readList() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

function writeList(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage can be unavailable (private mode); the UI still reflects the in-memory toggle.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
}

function onListChange(callback, signal) {
  document.addEventListener(CHANGE_EVENT, callback, { signal });
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) callback();
  }, { signal });
}

class SWishlistToggle extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', (event) => {
      // The card's stretched link sits underneath; keep the tap on the heart.
      event.preventDefault();
      event.stopPropagation();
      const handle = this.dataset.handle;
      if (!handle) return;
      const list = readList();
      const next = list.includes(handle) ? list.filter((item) => item !== handle) : [handle, ...list];
      writeList(next);
    }, { signal });

    onListChange(() => this.#sync(), signal);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync() {
    const button = this.querySelector('button');
    if (!button) return;
    const saved = readList().includes(this.dataset.handle);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }
}

class SWishlistCount extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    onListChange(() => this.#sync(), this.#ac.signal);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync() {
    const count = readList().length;
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  }
}

function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const withDecimals = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const noDecimals = Math.round(value).toLocaleString('en-US');
  const template = format || '{{amount}}';
  return template
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
    .replace(/\{\{\s*amount\s*\}\}/, withDecimals)
    .replace(/\{\{\s*[a-z_]+\s*\}\}/, withDecimals);
}

class SWishlistList extends HTMLElement {
  #ac;
  #cache = new Map();
  #renderToken = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-wishlist-remove]');
      if (!remove) return;
      writeList(readList().filter((handle) => handle !== remove.dataset.wishlistRemove));
    }, { signal });

    onListChange(() => this.#render(), signal);
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #fetchProduct(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const root = this.dataset.root || '/';
    const request = fetch(`${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    this.#cache.set(handle, request);
    return request;
  }

  async #render() {
    const token = ++this.#renderToken;
    const list = readList();
    const itemsEl = this.querySelector('[data-wishlist-items]');
    const emptyEl = this.querySelector('[data-wishlist-empty]');
    if (!itemsEl || !emptyEl) return;

    emptyEl.hidden = list.length > 0;
    if (list.length === 0) {
      itemsEl.replaceChildren();
      return;
    }

    const products = await Promise.all(list.map((handle) => this.#fetchProduct(handle)));
    if (token !== this.#renderToken) return;

    const fragment = document.createDocumentFragment();
    products.forEach((product, index) => {
      if (!product) return;
      fragment.append(this.#itemElement(product, list[index]));
    });
    itemsEl.replaceChildren(fragment);
    emptyEl.hidden = itemsEl.children.length > 0;
  }

  #itemElement(product, handle) {
    return this.dataset.layout === 'grid' ? this.#cardElement(product, handle) : this.#rowElement(product, handle);
  }

  #imageSrc(product, width) {
    if (!product.featured_image) return '';
    const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
    return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  }

  // Wishlist page card: mirrors the collection product card (3:4 image, serif-free title, price, actions).
  #cardElement(product, handle) {
    const li = document.createElement('li');
    li.className = 'flex flex-col gap-2';

    const media = document.createElement('a');
    media.href = product.url;
    media.className = 'relative block overflow-hidden bg-surface-secondary aspect-[3/4]';
    const src = this.#imageSrc(product, 600);
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.srcset = [300, 450, 600, 900].map((w) => `${this.#imageSrc(product, w)} ${w}w`).join(', ');
      img.sizes = '(min-width: 1024px) 25vw, 50vw';
      img.alt = product.title;
      img.loading = 'lazy';
      img.className = 'size-full object-cover';
      media.append(img);
    }

    const title = document.createElement('a');
    title.href = product.url;
    title.className = 'type-body-sm text-on-surface no-underline line-clamp-2 pt-1 hover:opacity-70';
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'text-sm text-on-surface/70';
    price.textContent = formatMoney(product.price, this.dataset.moneyFormat);

    const actions = document.createElement('div');
    actions.className = 'mt-2 flex flex-col gap-1';
    const buttonClass = 'button-outline inline-flex h-11 w-full items-center justify-center px-3 text-[11px] font-medium uppercase tracking-[0.2em] cursor-pointer';
    if (product.available) {
      // Opens the shared quick-add modal (same contract as the card's quick view: command + data-product-url).
      const add = document.createElement('button');
      add.type = 'button';
      add.className = buttonClass;
      add.setAttribute('commandfor', this.dataset.modalId || 'quick-add-modal');
      add.setAttribute('command', '--open-product');
      add.setAttribute('aria-haspopup', 'dialog');
      add.dataset.productUrl = product.url;
      add.textContent = this.dataset.addLabel || 'Add to cart';
      actions.append(add);
    } else {
      const soldOut = document.createElement('span');
      soldOut.className = `${buttonClass} opacity-50 cursor-default`;
      soldOut.textContent = this.dataset.soldOutLabel || 'Sold out';
      actions.append(soldOut);
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.wishlistRemove = handle;
    remove.className = 'min-h-touch-target text-[11px] uppercase tracking-[0.2em] text-on-surface/70 underline underline-offset-4 hover:text-on-surface cursor-pointer';
    remove.textContent = this.dataset.removeLabel || 'Remove';
    remove.setAttribute('aria-label', `${remove.textContent}: ${product.title}`);
    actions.append(remove);

    li.append(media, title, price, actions);
    return li;
  }

  #rowElement(product, handle) {
    const li = document.createElement('li');
    li.className = 'flex items-center gap-4 py-4';

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'block w-16 shrink-0 overflow-hidden bg-surface-secondary aspect-[3/4]';
    if (product.featured_image) {
      const img = document.createElement('img');
      const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
      img.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
      img.alt = product.title;
      img.loading = 'lazy';
      img.className = 'size-full object-cover';
      link.append(img);
    }

    const info = document.createElement('div');
    info.className = 'flex min-w-0 flex-1 flex-col gap-1';
    const title = document.createElement('a');
    title.href = product.url;
    title.className = 'type-body-sm text-on-surface no-underline line-clamp-2 hover:opacity-70';
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'text-sm text-on-surface/70';
    price.textContent = formatMoney(product.price, this.dataset.moneyFormat);
    info.append(title, price);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.wishlistRemove = handle;
    remove.className = 'shrink-0 min-h-touch-target px-2 text-[11px] uppercase tracking-[0.2em] text-on-surface/70 underline underline-offset-4 hover:text-on-surface cursor-pointer';
    remove.textContent = this.dataset.removeLabel || 'Remove';
    remove.setAttribute('aria-label', `${remove.textContent}: ${product.title}`);

    li.append(link, info, remove);
    return li;
  }
}

if (!customElements.get('s-wishlist-toggle')) customElements.define('s-wishlist-toggle', SWishlistToggle);
if (!customElements.get('s-wishlist-count')) customElements.define('s-wishlist-count', SWishlistCount);
if (!customElements.get('s-wishlist-list')) customElements.define('s-wishlist-list', SWishlistList);
